import React, { Component } from 'react'
import ReactHighcharts from 'react-highcharts'
import { injectIntl } from 'react-intl'

import DecoupledLinesChartConfig from './DecoupledLinesChartConfig'
import LinesChartAdapter from './LinesChartAdapter'

import AppTools from 'utils/app/appTools'
import DataTools from 'utils/dataTools'
import RegionTools from 'utils/regions/regionTools'
import Colors from 'utils/colors/colors'
import ObjectTools from 'utils/objects/objectTools'

import Loading from 'components/Loading'
import Error from 'components/Error'

class DecoupledLinesChart extends Component {
	constructor(props) {
		super(props);

		// console.log( props );

		const {
			intl: { formatMessage, formatNumber }
		} = props
		this.formatMessage = formatMessage
		this.formatNumber = formatNumber
		AppTools.importFormatter(formatMessage)

		this.state = {
			loading: true,
			error: false
		}
		this.chartRef = React.createRef()
	}

	componentDidMount() {
		this.props.fn(this.chartRef)
		const generalRegionsData = JSON.parse(this.props.generalRegionsData)
		const { regionsData, countriesData, referenceRegionsData, sameCountryRegionsData, comparative, allRegionsGroupRegionId } = generalRegionsData
		this.setState(
			{
				regionsData,
				countriesData,
				referenceRegionsData,
				sameCountryRegionsData,
				comparative,
				allRegionsGroupRegionId
			},
			() => this.getLinesChart()
		)
	}

	shouldComponentUpdate(nextProps, nextState) {
		if (this.props.showLegend !== nextProps.showLegend) return false
		const shouldUpdate = !ObjectTools.compareObjects(this.props, nextProps) || !ObjectTools.compareObjects(this.state, nextState)
		return shouldUpdate
	}

	componentDidUpdate(prevProps) {
		if (!ObjectTools.compareObjects(this.props, prevProps)) {
			const generalRegionsData = JSON.parse(this.props.generalRegionsData)
			const { regionsData, referenceRegionsData, sameCountryRegionsData, comparative, allRegionsGroupRegionId } = generalRegionsData
			this.setState(
				{
					regionsData,
					referenceRegionsData,
					sameCountryRegionsData,
					comparative,
					allRegionsGroupRegionId,
					loading: true,
					error: false
				},
				() => this.getLinesChart()
			)
		}
	}

	getRegionMeta = ( regionId, geosMeta ) => {
		let regionMeta = geosMeta.find(item => item.id === regionId);
		return regionMeta;
	}

	getRegionsToPlot = ( geosMeta ) => {
		let regionDefs = [];
		if (this.props.regionsToPlot) {			
			this.props.regionsToPlot.forEach( region => {
				let regionMeta = this.getRegionMeta( region.id, geosMeta );
				regionDefs.push({
					regionId: region.id,
					regionType: "selected",
					name: regionMeta.name,
					dashStyle: region.style || "solid"
				});
			} );
		}

		return regionDefs;
	}

	_getGeoLocalizedName( geo_id, locale ) {
		const names = {
			1: {
				"es": "País Vasco",
				"en": "Basque Country",
				"eu": "EAE"
			},
			411: {
				"es": "España",
				"en": "Spain",
				"eu": "Espainia"
			},
			325: {
				"es": "Alemania",
				"en": "Germany",
				"eu": "Alemania"
			},
			2692: {
				"es": "UE-27",
				"en": "EU-27",
				"eu": "EB-27"
			},
			326: {
				"es": "Baden-Wurtemberg",
				"en": "Baden-Würtemberg",
				"eu": "Baden-Wurtemberg"
			},
			560: {
				"es": "Alta Austria",
				"en": "Upper Austria",
				"eu": "Austria Garaia"
			}
		};

		return names[geo_id][locale];
	}

	getLinesChart = ({ indicator, selectedRegionId } = { indicator: this.props.indicator, selectedRegionId: this.props.selectedRegionId }) => {
		let regionsData = this.state.regionsData;
		let countriesData = this.state.countriesData;
		let geoMeta = regionsData.concat( countriesData );

		// // let referenceRegionsData = this.state.referenceRegionsData
		// //   let sameCountryRegionsData = this.state.sameCountryRegionsData
		// let comparative = this.state.comparative
		let allRegionsGroupRegionId = this.state.allRegionsGroupRegionId;

		// const linesToShow = JSON.parse(this.props.linesToShow)
		// const { allRegionsFilter, /* referenceRegionsFilter, */ sameCountryRegionsFilter, otherRegionsFilter } = linesToShow
		// let comparativeShouldShow = Object.keys(otherRegionsFilter).length !== 0
		// const showByGender = this.props.showByGender
		// let indicatorFemale = 0
		// let indicatorMale = 0
		// if (showByGender) {
		// 	indicatorFemale = this.props.indicatorData.relationships.sexFemale
		// 	indicatorMale = this.props.indicatorData.relationships.sexMale
		// }

		// dynamic country code: Usar el selectedCountryMeta.id para ID

		let selectedRegionData = regionsData.filter(item => item.id === selectedRegionId)[0]
		let selectedCountryCode = RegionTools.getCountryCodeFromRegionCode(selectedRegionData.code)
		let selectedCountryMeta = RegionTools.countryCodeToMeta(selectedCountryCode);
		// let selectedCountryName = RegionTools.countryCodeToFullName(selectedCountryCode)

		let chartConfig = this.selectConfig('selectedRegion').chartConfig

		// const promises = []

		let regionsToPlot = this.getRegionsToPlot( geoMeta );

		let currentLocale = this.props.intl.locale;

		if (regionsToPlot.length === 0) {
			regionsToPlot = [{
				regionId: selectedRegionId,
				regionType: "selected",
				name: this._getGeoLocalizedName( 1, currentLocale ), // "País Vasco",
				dashStyle: "solid"
			}, {
				regionId: selectedCountryMeta.id, // 411
				regionType: "country",
				name: this._getGeoLocalizedName( 411, currentLocale ), //  "España",
				dashStyle: "Dash"
			}, {
				regionId: 325,
				regionType: "country",
				name: this._getGeoLocalizedName( 325, currentLocale ), //  "Alemania",
				dashStyle: "Dash"
			}, {
				regionId: allRegionsGroupRegionId, // 2692,
				regionType: "sup-country",
				name: this._getGeoLocalizedName( 2692, currentLocale ), //  "UE-27",
				dashStyle: "LongDash"
			}, {
				regionId: 326,
				regionType: "sub-country",
				name: this._getGeoLocalizedName( 326, currentLocale ), //  "Baden-Wurtemberg",
				dashStyle: "solid"
			}, {
				regionId: 560,
				regionType: "sub-country",
				name: this._getGeoLocalizedName( 560, currentLocale ), //  "Alta Austria",
				dashStyle: "solid"
			} ];
		}

		regionsToPlot.forEach( lineItem => {
			let geo = lineItem.regionId;
			lineItem.data = this.props.data.filter( (item) => item.geo === geo );
		} );

		let extent = [
			this.props.startingYear,
			this.props.endingYear
		];

		let commonTimes = DataTools.intersection( 
			regionsToPlot
				.filter( lineItem => lineItem.data && lineItem.data.length > 0 )
				.map( lineItem => { let uv = DataTools.uniquePropertyValues( lineItem.data, "time_year" ); return uv; }) );

		let lastCommonYear = Math.max( ...commonTimes.filter( (y) => ((y >= extent[0]) && (y <= extent[1])) ) );
		// console.log( commonTimes, lastCommonYear );

		let series = regionsToPlot.map((lineItem, index) => {
			let chartColor = Colors.getColorByRegionId(lineItem.regionId);
			let name = lineItem.name;

			let regionData = lineItem.data;

			if ( regionData && regionData.length ) {
				return LinesChartAdapter.formatLinesSeries( {
					name: name,
					data: regionData,
					color: chartColor,
					extent: extent,
					lastCommonYear: lastCommonYear,
					dashedLineStyle: lineItem.dashStyle,
					formatMessage: this.formatMessage
				} );
			} else {
				return null;
			}
		});

		let customLinesConfig = ObjectTools.copyObject(chartConfig)

		series = series.filter( v => v !== null );

		customLinesConfig.series = series;
		if (this.props.expanded) {
			let panelHeight = document.querySelector('.panel-expanded').offsetHeight
			let panelHeaderHeight = document.querySelector('.panel-expanded>.panel-heading').offsetHeight
			let panelFooterHeight = document.querySelector('.panel-expanded>.panel-footer').offsetHeight
			let panelBody = document.querySelector('.panel-expanded>.panel-body')
			let paddingTop = parseInt(window.getComputedStyle(panelBody).getPropertyValue('padding-top'), 10)
			let paddingBottom = parseInt(window.getComputedStyle(panelBody).getPropertyValue('padding-bottom'), 10)
			let panelBodyHeight = panelHeight - panelHeaderHeight - panelFooterHeight - paddingTop - paddingBottom
			customLinesConfig.chart.height = `${panelBodyHeight}px`
		}

		let dataSource = this.props.indicatorData.dataSource;
		dataSource = dataSource ? dataSource : "Sin definir";

		customLinesConfig.legend.enabled = this.props.showLegend
		customLinesConfig.credits.text = dataSource + "<br/>Elaboración Orkestra";// AppTools.getComposedDataSource( dataSource );
		customLinesConfig.yAxis.title.text = this.props.indicatorData.unit
		const formatNumber = this.formatNumber
		const unit = this.props.indicatorData.unit;
		// console.log(this.props)
		customLinesConfig.tooltip.pointFormatter = function () {
			let tooltip = '<span style="color:' + this.color + '">●</span><b>' + this.series.name + '</b>: ' + formatNumber(this.y.toFixed(2)) + ' ' + unit;
			if ( this.notes ) {
				tooltip += '<br/>' + this.notes;
			}
			return tooltip;
		}

		this.setState({ chartConfig: customLinesConfig, loading: false });
	}

	selectConfig = (config, index) => {
		switch (config) {
			case 'selectedRegion':
				return {
					chartConfig: DecoupledLinesChartConfig.selectedRegionConfig,
					chartColor: Colors.selectedRegionColor
				}

			case 'allRegions':
				return {
					chartConfig: DecoupledLinesChartConfig.allRegionsConfig,
					chartColor: Colors.allRegionsColor
				}

			case 'referenceRegions':
				return {
					chartConfig: DecoupledLinesChartConfig.referenceRegionsConfig,
					chartColor: Colors.referenceRegionsColor
				}
			case 'sameCountryRegions':
				return {
					chartConfig: DecoupledLinesChartConfig.sameCountryRegionsConfig,
					chartColor: Colors.sameCountryRegionsColor
				}
			case 'otherRegions':
				return {
					chartConfig: DecoupledLinesChartConfig.otherRegionsConfig,
					chartColor: Colors.selectOtherRegionsColor(index)
				}

			default:
				return this.selectConfig('allRegions')
		}
	}

	renderIfAllOK = () => {
		let renderingComponent = null
		if (this.state.error) {
			renderingComponent = <Error />
		} else if (this.state.loading) {
			renderingComponent = <Loading />
		} else {
			renderingComponent = <ReactHighcharts ref={this.chartRef} config={this.state.chartConfig} />
		}
		return renderingComponent
	}
	render() {
		return <React.Fragment>{this.renderIfAllOK()}</React.Fragment>
	}
}

DecoupledLinesChart.defaultProps = {
	regionsToPlat: null,		// array of dicts [{id:, style:}],
	geoIds: null
};

export default injectIntl(DecoupledLinesChart)



// WEBPACK FOOTER //
// ./src/modules/Charts/DecoupledLines/DecoupledLinesChart/DecoupledLinesChart.jsx